/**
 * <SharedThreadPage /> — read-only view of a shared chat thread
 * (M25.6).
 *
 * Reached via a tenant-scoped share link (/ai/shared/:code). The
 * recipient sees the conversation history as a snapshot — no input,
 * no controls, can't reply. The share code only resolves inside the
 * tenant, so sharing never crosses tenant boundaries.
 */

import { Share2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { ChatMessage } from "../components/chat/ChatMessage";
import type { ChatTurn } from "../components/chat/types";
import { ErrorPanel, Loading } from "../components/Loading";
import { PageHeader } from "../components/ui";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";

export function SharedThreadPage() {
  const { t } = useTranslation();
  const { code } = useParams<{ code: string }>();

  const q = useQuery({
    queryKey: ["shared-thread", code],
    queryFn: () => api.getSharedThread(code as string),
    enabled: Boolean(code),
  });

  if (q.isLoading) return <Loading />;
  if (q.error)
    return (
      <ErrorPanel message={t("policies.shared_thread_unavailable")} />
    );
  if (!q.data) return null;

  const turns = (q.data.transcript as unknown as ChatTurn[]).map(
    (turn) => ({ ...turn, streaming: false }),
  );

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={q.data.name}
        subtitle={t(
          q.data.message_count === 1
            ? "policies.shared_thread_subtitle_full_one"
            : "policies.shared_thread_subtitle_full_many",
          { count: q.data.message_count },
        )}
        breadcrumbs={[{ label: t("nav.ai"), href: "/ai" }]}
      />

      <div className="flex items-start gap-2 rounded-lg border border-ai-subtle bg-ai-soft px-3 py-2 text-xs text-text-secondary">
        <Share2 size={13} className="mt-px shrink-0 text-brand-blue" />
        <span>
          {t("policies.shared_thread_readonly_note")}
        </span>
      </div>

      <div className="flex flex-col gap-4 rounded-xl border border-border-subtle bg-surface-default p-4">
        {turns.map((turn) => (
          <ChatMessage key={turn.id} turn={turn} />
        ))}
      </div>
    </div>
  );
}
